/**
 * scopedBlob.ts — legacy-array vs per-portfolio-record format negotiation.
 *
 * A portfolio-scoped list (trades, watchlist, snapshots, journal, thinkPieces) is
 * persisted as either `T[]` (legacy single-portfolio) or `{ [portfolioId]: T[] }`
 * (multi-portfolio). Every reader/writer must handle BOTH shapes while preserving
 * the stored one — this cross-runtime module of PURE functions gives that contract
 * one home (app/src/lib/scopedBlob.ts re-exports it; the Pages Functions write
 * guard consumes it server-side).
 */

export type ScopedBlob<T> = T[] | Record<string, T[]>;

/**
 * Resolve a scoped blob to a single portfolio's slice. Array form returns as-is.
 * Record form returns the requested portfolio's array. When a SPECIFIC
 * `portfolioId` is asked for but absent, return an empty slice — never another
 * portfolio's data (a new portfolio with no trades yet must not show a sibling's
 * holdings). The first-key fallback applies ONLY to the legacy no-id case.
 */
export function resolveScopedBlob<T>(blob: ScopedBlob<T>, portfolioId?: string): T[] {
  if (Array.isArray(blob)) return blob;
  if (portfolioId !== undefined) return Array.isArray(blob[portfolioId]) ? blob[portfolioId] : [];
  const firstKey = Object.keys(blob)[0];
  return firstKey ? (blob[firstKey] ?? []) : [];
}

/**
 * Compute the next blob to persist when replacing one portfolio's slice with
 * `items`, preserving the stored shape: an array stays an array when no
 * `portfolioId` is given (legacy single-portfolio users keep the old shape),
 * otherwise migrates to a record; a record preserves other portfolios' slices.
 * Pure: returns the body to POST; does no I/O.
 */
export function mergeScopedBlob<T>(
  current: ScopedBlob<T>,
  items: T[],
  portfolioId?: string,
): ScopedBlob<T> {
  if (Array.isArray(current)) {
    return portfolioId ? { [portfolioId]: items } : items;
  }
  const next = { ...current };
  const key = portfolioId ?? Object.keys(current)[0] ?? '_default';
  next[key] = items;
  return next;
}

/**
 * Structural shape check for a value claiming to be a scoped blob, used by the
 * Pages Functions write guard. Returns the first violation message, or null when
 * acceptable. `checkEntry` is applied to every row in key order, interleaved with
 * the per-portfolio array check — the traversal order the write guard has always
 * used, so error precedence on doubly-corrupt payloads is unchanged.
 */
export function checkScopedBlobShape(
  value: unknown,
  checkEntry?: (row: unknown) => string | null,
): string | null {
  const check = checkEntry ?? (() => null);
  if (Array.isArray(value)) {
    for (const row of value) {
      const err = check(row);
      if (err) return err;
    }
    return null;
  }
  if (typeof value === 'object' && value !== null) {
    for (const [pid, slice] of Object.entries(value)) {
      if (!Array.isArray(slice)) return `portfolio "${pid}" must map to an array`;
      for (const row of slice) {
        const err = check(row);
        if (err) return err;
      }
    }
    return null;
  }
  return 'must be an array or a {portfolioId: [...]} object';
}
